import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TotpChallengeForm } from '@f2m3/shared-frontend-ui';
import PasswordLogin from './PasswordLogin';
import { useMfaVerify } from '../../api/queryHooks/mfa';
import { apiErrorKey } from '../../utils/apiErrorKey';
import notify from '../../utils/notify';

interface PasswordLoginFlowProps {
  email: string;
}

interface Challenge {
  mfaToken: string;
  methods: string[];
}

// Terminal errors mean the challenge token itself is dead (expired or the
// backend locked it out after too many attempts) — there is no point staying
// on the challenge screen, so we bounce the user back to the password step.
const TERMINAL_ERROR_KEYS = ['notify.mfa.invalid_or_expired', 'notify.mfa.too_many_attempts'];

// Orchestrates the two-step password login: password form, then (only when the
// backend returns mfa_required) an inline TOTP/recovery challenge. On verify
// success the newToken callback stores the session token and the Login page's
// redirect effect fires — no navigation needed here.
const PasswordLoginFlow = ({ email }: PasswordLoginFlowProps) => {
  const { t } = useTranslation();
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [rememberDevice, setRememberDevice] = useState(false);
  const mfaVerify = useMfaVerify();

  if (!challenge) {
    return <PasswordLogin email={email} onMfaRequired={setChallenge} />;
  }

  return (
    <div>
      <h2 className="is-size-5 mb-4">{t('login.mfa.title')}</h2>
      <TotpChallengeForm
        methods={challenge.methods}
        isPending={mfaVerify.isPending}
        error={mfaVerify.isError ? t(apiErrorKey(mfaVerify.error, 'notify.mfa.invalid_code')) : null}
        rememberDevice={rememberDevice}
        onRememberDeviceChange={setRememberDevice}
        onSubmit={(method, code, remember) =>
          mfaVerify.mutate(
            { mfaToken: challenge.mfaToken, method, code, rememberDevice: remember },
            {
              onError: (err) => {
                const key = apiErrorKey(err, 'notify.mfa.invalid_code');
                if (TERMINAL_ERROR_KEYS.includes(key)) {
                  notify('notify.error', key, { type: 'warning' });
                  setChallenge(null);
                  mfaVerify.reset();
                }
              },
            },
          )
        }
        labels={{
          codeLabel: t('login.mfa.code_label'),
          codePlaceholder: t('login.mfa.code_placeholder'),
          recoveryLabel: t('login.mfa.recovery_label'),
          recoveryPlaceholder: t('login.mfa.recovery_placeholder'),
          verify: t('login.mfa.verify'),
          useRecovery: t('login.mfa.use_recovery'),
          useAuthenticator: t('login.mfa.use_authenticator'),
          rememberDevice: t('login.mfa.remember_device'),
        }}
      />
    </div>
  );
};

export default PasswordLoginFlow;
